/**
 * The settings this watch's own advice tells you to change.
 *
 * THE VERDICT PANEL SITS ABOVE THIS AND NAMES THESE KNOBS BY THEIR KEYS — "raise
 * `new_page_min`, currently the default 3, to about 6". Until this existed there was
 * nowhere to type the 6. That is the whole reason it is on the same screen and in the
 * same words: the advice and the control have to be in one place or the advice is a
 * complaint.
 *
 * THE VALUE AND ITS PROVENANCE ARE THE SERVER'S WORDS. "3 (default)" versus "10 (your
 * setting on this subject)" is written in `thresholds.py`, beside the rule that uses it,
 * so this screen cannot drift from what actually applies. A bare number here would leave
 * somebody unable to tell their own setting from an inherited one — which is how they end
 * up re-typing a default and quietly owning it.
 *
 * COLLAPSED BY DEFAULT. Most people never touch a threshold, and a wall of number boxes
 * above the alerts would make the page look like a configuration screen rather than a
 * feed.
 */
import { useState } from 'react';
import api from '../../services/api';
import { byFamily, changedKnobs, explain, knobsFor } from './knobs';
import type { WatchCatalogue, WatchSubject } from '../../types';

export default function KnobsPanel({
  subject,
  catalogue,
  onSaved,
}: {
  subject: WatchSubject;
  catalogue: WatchCatalogue | null;
  onSaved: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const knobs = knobsFor(subject.thresholds_in_play ?? [], catalogue);
  const muted = subject.muted_kinds ?? [];
  const groups = byFamily(knobs, catalogue);
  const pending = changedKnobs(knobs, edits);
  const count = Object.keys(pending).length;

  if (!knobs.length) return null;

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      // THE SERVER REPLACES THE WHOLE MAP, so the existing overrides are sent with the
      // changes — dropping them would silently reset every knob the user had already set.
      // A `null` in `pending` is how a removal travels: non-numbers are dropped, so that
      // key ends up absent from the stored map.
      await api.patchWatchSubject(subject.id, {
        thresholds: { ...(subject.thresholds ?? {}), ...pending },
      });
      setEdits({});
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That did not save');
    } finally {
      setSaving(false);
    }
  };

  const unmute = async (kind: string) => {
    setSaving(true);
    try {
      await api.patchWatchSubject(subject.id, {
        muted_kinds: muted.filter((k) => k !== kind),
      });
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That did not save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="mb-6" data-testid="watch-knobs">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="text-xs font-semibold uppercase tracking-wide text-gray-500 underline dark:text-gray-400"
      >
        {open ? 'Hide settings' : 'How loud this watch is'}
      </button>

      {open && (
        <div className="mt-2 space-y-4">
          {groups.map((group) => (
            <div key={group.family}>
              <h3 className="mb-1 text-[11px] font-semibold text-gray-600 dark:text-gray-300">
                {group.title}
              </h3>
              <ul className="space-y-2">
                {group.knobs.map((knob) => (
                  <li key={knob.key} className="flex flex-wrap items-baseline gap-2">
                    <input
                      type="text"
                      inputMode="decimal"
                      value={edits[knob.key] ?? (knob.isOverride ? String(knob.value ?? '') : '')}
                      onChange={(e) =>
                        setEdits((m) => ({ ...m, [knob.key]: e.target.value }))
                      }
                      /* The current value as the placeholder when it is inherited, so an empty
                         box reads as "following the default" rather than as unset. */
                      placeholder={String(knob.value ?? '')}
                      className="w-16 rounded border border-gray-300 px-2 py-0.5 text-xs dark:border-gray-600 dark:bg-gray-900"
                      aria-label={knob.key}
                    />
                    <span className="text-[11px] text-gray-700 dark:text-gray-200">
                      <code>{knob.key}</code>
                      <span className="ml-1 text-gray-500 dark:text-gray-400">
                        {knob.described}
                      </span>
                    </span>
                    <span className="w-full text-[11px] text-gray-500 dark:text-gray-400">
                      {explain(knob)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {muted.length > 0 && (
            <div>
              <h3 className="mb-1 text-[11px] font-semibold text-gray-600 dark:text-gray-300">
                Muted here
              </h3>
              {/* Visible and reversible: somebody who muted something in March and hears
                  nothing in September has no way to tell a mute from a broken check. */}
              <ul className="flex flex-wrap gap-1">
                {muted.map((kind) => (
                  <li key={kind}>
                    <button
                      type="button"
                      disabled={saving}
                      onClick={() => void unmute(kind)}
                      className="rounded bg-gray-100 px-1.5 py-0.5 text-[11px] text-gray-600 disabled:opacity-40 dark:bg-gray-800 dark:text-gray-300"
                      title="Unmute"
                    >
                      {kind.replace(/_/g, ' ')} ×
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {error && (
            <p className="text-[11px] text-red-600 dark:text-red-400" role="alert">
              {error}
            </p>
          )}

          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={saving || count === 0}
              onClick={() => void save()}
              className="rounded bg-gray-900 px-2 py-1 text-[11px] text-white disabled:opacity-40 dark:bg-gray-100 dark:text-gray-900"
            >
              {saving ? 'Saving…' : count ? `Save ${count} change${count > 1 ? 's' : ''}` : 'Save'}
            </button>
            <span className="text-[11px] text-gray-500 dark:text-gray-400">
              Clear a box to go back to the default.
            </span>
          </div>
        </div>
      )}
    </section>
  );
}
